// src/components/AuthenticationWrapper.tsx
import React, { useEffect, useRef } from 'react';
import { Amplify } from 'aws-amplify';
import { Authenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';

Amplify.configure({
    Auth: {
        Cognito: {
            userPoolId: process.env.REACT_APP_USER_POOL_ID!,
            userPoolClientId: process.env.REACT_APP_USER_POOL_CLIENT_ID!,

            loginWith: {
                email: true,      // allow email/password
                username: false,   // not allow username
                oauth: {          // Hosted UI / social config
                    domain:           process.env.REACT_APP_AUTH_DOMAIN!,
                    scopes:           ['openid', 'email', 'profile'],
                    redirectSignIn:   [process.env.REACT_APP_REDIRECT_SIGN_IN!],
                    redirectSignOut:  [process.env.REACT_APP_REDIRECT_SIGN_OUT!],
                    responseType:     'code',
                    providers: [
                        'Google', { custom: 'Cognito' }
                    ]
                },
            },
        },
    },
});

/**
 * Wraps the Amplify Authenticator. The dialog's accessible name comes from the
 * surrounding Cloudscape Modal (header="Sign in to TradeDigger" in
 * NavigationComponent), which already provides the heading and dialog label —
 * so no inner h2 or aria-label is needed here.
 *
 * We also apply noValidate (B3) to suppress browser-native validation popups
 * in favour of Amplify/Cloudscape error messages, and we surface Cognito's
 * password rules (B2) on the sign-up screen.
 */
export const JuhsisAuthenticator: React.FC = () => {
    const containerRef = useRef<HTMLDivElement | null>(null);

    // B3: Amplify renders the form internally, so we cannot pass `noValidate`
    // as a prop. The lightest-weight workaround is to set the attribute after
    // the form mounts. We watch for the form on every render of the
    // authenticator (sign-in vs sign-up swap) via a MutationObserver.
    useEffect(() => {
        const root = containerRef.current;
        if (!root) return;

        const apply = () => {
            const forms = root.querySelectorAll('form');
            forms.forEach((form) => {
                if (!form.hasAttribute('novalidate')) {
                    form.setAttribute('novalidate', 'true');
                }
            });
        };

        apply();
        const observer = new MutationObserver(apply);
        observer.observe(root, { childList: true, subtree: true });
        return () => observer.disconnect();
    }, []);

    return (
        <div ref={containerRef}>
            <Authenticator
                loginMechanisms={['email']}
                socialProviders={['google']}
                components={{ SignUp: { FormFields: CustomSignUpFields } }}
            />
        </div>
    );
};

function CustomSignUpFields() {
    return (
        <>
            <Authenticator.SignUp.FormFields />
            <div style={{ marginTop: '1rem', textAlign: 'left' }}>
                <label htmlFor="name">Full Name</label>
                <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    placeholder="Please enter your full name"
                    style={{ width: '100%', padding: '0.5rem' }}
                />
            </div>
            {/* B2: Surface Cognito's actual default password rules. These match
                the pool config in infrastructure/src/stack/cognito-stack.ts
                (no overrides → Cognito defaults: 8+ chars, mixed case, number, symbol). */}
            <p
                id="password-rules"
                style={{
                    marginTop: '0.75rem',
                    fontSize: '0.85rem',
                    color: '#5f6b7a',
                    lineHeight: 1.4,
                }}
            >
                Password must be at least 8 characters and include an uppercase letter,
                a lowercase letter, a number, and a symbol.
            </p>
            <p
                style={{
                    marginTop: '0.75rem',
                    fontSize: '0.8rem',
                    color: '#5f6b7a',
                    lineHeight: 1.4,
                }}
            >
                {/* Tranche 1 placeholder: terms page does not yet exist; flag B2 follow-up.
                    Using a disabled-looking inline button rather than href="#" to keep
                    the lint clean while still surfacing the copy the user needs. */}
                By creating an account you agree to our{' '}
                <button
                    type="button"
                    aria-label="Terms of service (placeholder, not yet linked)"
                    onClick={() => { /* TODO(B2): wire to /terms once a terms page exists. */ }}
                    style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        color: '#0972d3',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        font: 'inherit',
                    }}
                >
                    terms
                </button>.
            </p>
        </>
    );
}

